import React from 'react';
import { Box, Collapse, Flex, Group, Paper, Text, ThemeIcon, Title, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useLocalStorage } from '@mantine/hooks';
import classes from './CollapsibleSection.module.css';

interface ICollapsibleSectionProps {
  title: string;
  storageKey: string;
  subtitle?: string;
  icon?: React.ReactNode;
  showLabel?: string;
  hideLabel?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<ICollapsibleSectionProps> = ({
  title,
  storageKey,
  subtitle,
  icon,
  showLabel = 'Show',
  hideLabel = 'Hide',
  defaultOpen = true,
  children,
}) => {
  const [opened, setOpened] = useLocalStorage({ key: storageKey, defaultValue: defaultOpen });

  return (
    <Paper withBorder p={0} style={{ overflow: 'hidden' }}>
      <UnstyledButton
        className={classes.header}
        aria-label={title}
        aria-expanded={opened}
        onClick={() => setOpened(!opened)}
      >
        <Flex align="center" gap="md" p="md">
          {icon && (
            <ThemeIcon variant="light" color="blue" size={42} radius="md">
              {icon}
            </ThemeIcon>
          )}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Title order={4}>{title}</Title>
            {subtitle && (
              <Text size="sm" c="dimmed" mt={2}>
                {subtitle}
              </Text>
            )}
          </Box>
          <Group className={classes.toggle} gap={6} wrap="nowrap" py={6} px="sm">
            <Text size="sm" fw={600} c="blue">
              {opened ? hideLabel : showLabel}
            </Text>
            <IconChevronDown
              size={18}
              className={`${classes.chevron} ${opened ? classes.chevronOpen : ''}`}
            />
          </Group>
        </Flex>
      </UnstyledButton>

      <Collapse in={opened} transitionDuration={300}>
        <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default CollapsibleSection;
